import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import ScrollToTop from "@/components/ScrollToTop";
import ErrorBoundary from "@/components/ErrorBoundary";
import OfflineBanner from "@/components/OfflineBanner";
import TrialExpiryBanner from "@/components/TrialExpiryBanner";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import DashboardPage from "@/pages/DashboardPage";
import JournalPage from "@/pages/JournalPage";
import ExercisesPage from "@/pages/ExercisesPage";
import EmotionsPage from "@/pages/EmotionsPage";
import MotivationPage from "@/pages/MotivationPage";
import TriggersPage from "@/pages/TriggersPage";
import SettingsPage from "@/pages/SettingsPage";
import SubscriptionPage from "@/pages/SubscriptionPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";
import CookiePolicyPage from "@/pages/CookiePolicyPage";
import MarketingPage from "@/pages/MarketingPage";
import CrisisPage from "@/pages/CrisisPage";
import ReflectionsPage from "@/pages/ReflectionsPage";
import SelfDiscoveryPage from "@/pages/SelfDiscoveryPage";
import GratitudePage from "@/pages/GratitudePage";
import RelationshipsPage from "@/pages/RelationshipsPage";
import BoundariesPage from "@/pages/BoundariesPage";
import LettersPage from "@/pages/LettersPage";
import FitnessPage from "@/pages/FitnessPage";
import NutritionPage from "@/pages/NutritionPage";
import SleepPage from "@/pages/SleepPage";
import MemoryPage from "@/pages/MemoryPage";
import FocusPage from "@/pages/FocusPage";
import FinancePage from "@/pages/FinancePage";
import RoutinesPage from "@/pages/RoutinesPage";
import StepsPage from "@/pages/StepsPage";
import HormoneTrackingPage from "@/pages/HormoneTrackingPage";
import GoalsPage from "@/pages/GoalsPage";
import MeditationPage from "@/pages/MeditationPage";
import RemindersPage from "@/pages/RemindersPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import CleaningPage from "@/pages/CleaningPage";
import ShoppingPage from "@/pages/ShoppingPage";
import HabitsPage from "@/pages/HabitsPage";
import NotesPage from "@/pages/NotesPage";
import TodoPage from "@/pages/TodoPage";
import WaterPage from "@/pages/WaterPage";
import MoodPage from "@/pages/MoodPage";
import ImportantDatesPage from "@/pages/ImportantDatesPage";
import WeekPlannerPage from "@/pages/WeekPlannerPage";
import CareerPage from "@/pages/CareerPage";
import CommunicationPage from "@/pages/CommunicationPage";
import ConflictPage from "@/pages/ConflictPage";
import CoupleExercisesPage from "@/pages/CoupleExercisesPage";
import FamilyPage from "@/pages/FamilyPage";
import SelfLovePage from "@/pages/SelfLovePage";
import AttachmentPage from "@/pages/AttachmentPage";
import FriendshipPage from "@/pages/FriendshipPage";
import ToxicRelationshipsPage from "@/pages/ToxicRelationshipsPage";
import SeparationPage from "@/pages/SeparationPage";
import RelationshipCheckPage from "@/pages/RelationshipCheckPage";
import MaintenancePage from "@/pages/MaintenancePage";
import ProjectsPage from "@/pages/ProjectsPage";
import InventoryPage from "@/pages/InventoryPage";
import SavingsPage from "@/pages/SavingsPage";
import DebtsPage from "@/pages/DebtsPage";
import JobSearchPage from "@/pages/JobSearchPage";
import CvHelpPage from "@/pages/CvHelpPage";
import EconomyCheckPage from "@/pages/EconomyCheckPage";
import MealPlannerPage from "@/pages/MealPlannerPage";
import HealthLogPage from "@/pages/HealthLogPage";
import CategoryPage from "@/pages/CategoryPage";
import StatsPage from "@/pages/StatsPage";
import WorkoutHistoryPage from "@/pages/WorkoutHistoryPage";
import AdminPage from "@/pages/AdminPage";
import VoiceRecordingPage from "@/pages/VoiceRecordingPage";
import ProgramDetailPage, { ProgramsListPage } from "@/pages/ProgramsPage";
import CertificatesPage from "@/pages/CertificatesPage";
import WeeklySummaryPage from "@/pages/WeeklySummaryPage";
import ReferralPage from "@/pages/ReferralPage";
import InstallPrompt from "@/components/InstallPrompt";
import UpdatePrompt from "@/components/UpdatePrompt";

// App version - update this when deploying new features
export const APP_VERSION = '1.2.0';

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-primary/20 animate-breathe" />
      </div>
    );
  }
  
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth?next=${next}`} replace />;
  }

  return (
    <>
      <TrialExpiryBanner />
      {children}
    </>
  );
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-16 h-16 rounded-full bg-primary/20 animate-breathe" />
      </div>
    );
  }
  
  if (user) {
    // Honor ?next= when already logged in (e.g., a "Lämna recension" link bounced an authed user)
    try {
      const params = new URLSearchParams(location.search);
      const next = params.get('next');
      const review = params.get('review');
      if (next) {
        const suffix = review ? (next.includes('?') ? `&review=${review}` : `?review=${review}`) : '';
        return <Navigate to={`${next}${suffix}`} replace />;
      }
    } catch (e) { /* ignore */ }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  // Apply dark mode on app load
  useEffect(() => {
    const isDarkMode = localStorage.getItem('dark-mode') === 'true';
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  return (
    <div className="app-frame">
      <ErrorBoundary>
        <AuthProvider>
          <OfflineBanner />
          <BrowserRouter>
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<PublicRoute><AuthPage /></PublicRoute>} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookies" element={<CookiePolicyPage />} />
            <Route path="/marketing" element={<MarketingPage />} />
            <Route path="/crisis" element={<CrisisPage />} />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/category/:categoryId" element={<ProtectedRoute><CategoryPage /></ProtectedRoute>} />
            
            {/* Mina tankar & känslor */}
            <Route path="/journal" element={<ProtectedRoute><JournalPage /></ProtectedRoute>} />
            <Route path="/reflections" element={<ProtectedRoute><ReflectionsPage /></ProtectedRoute>} />
            <Route path="/emotions" element={<ProtectedRoute><EmotionsPage /></ProtectedRoute>} />
            <Route path="/gratitude" element={<ProtectedRoute><GratitudePage /></ProtectedRoute>} />
            <Route path="/self-discovery" element={<ProtectedRoute><SelfDiscoveryPage /></ProtectedRoute>} />
            <Route path="/triggers" element={<ProtectedRoute><TriggersPage /></ProtectedRoute>} />
            <Route path="/mood" element={<ProtectedRoute><MoodPage /></ProtectedRoute>} />
            <Route path="/attachment" element={<ProtectedRoute><AttachmentPage /></ProtectedRoute>} />
            <Route path="/self-love" element={<ProtectedRoute><SelfLovePage /></ProtectedRoute>} />
            
            {/* Min hälsa */}
            <Route path="/fitness" element={<ProtectedRoute><FitnessPage /></ProtectedRoute>} />
            <Route path="/workout-history" element={<ProtectedRoute><WorkoutHistoryPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
            <Route path="/voice" element={<ProtectedRoute><VoiceRecordingPage /></ProtectedRoute>} />
            <Route path="/weekly-summary" element={<ProtectedRoute><WeeklySummaryPage /></ProtectedRoute>} />
            <Route path="/referral" element={<ProtectedRoute><ReferralPage /></ProtectedRoute>} />
            <Route path="/sleep" element={<ProtectedRoute><SleepPage /></ProtectedRoute>} />
            <Route path="/steps" element={<ProtectedRoute><StepsPage /></ProtectedRoute>} />
            <Route path="/hormones" element={<ProtectedRoute><HormoneTrackingPage /></ProtectedRoute>} />
            <Route path="/nutrition" element={<ProtectedRoute><NutritionPage /></ProtectedRoute>} />
            <Route path="/water" element={<ProtectedRoute><WaterPage /></ProtectedRoute>} />
            <Route path="/meal-planner" element={<ProtectedRoute><MealPlannerPage /></ProtectedRoute>} />
            <Route path="/health-log" element={<ProtectedRoute><HealthLogPage /></ProtectedRoute>} />
            <Route path="/stats" element={<ProtectedRoute><StatsPage /></ProtectedRoute>} />
            
            {/* Mitt lugn */}
            <Route path="/exercises" element={<ProtectedRoute><ExercisesPage /></ProtectedRoute>} />
            <Route path="/meditation" element={<ProtectedRoute><MeditationPage /></ProtectedRoute>} />
            <Route path="/focus" element={<ProtectedRoute><FocusPage /></ProtectedRoute>} />
            <Route path="/memory" element={<ProtectedRoute><MemoryPage /></ProtectedRoute>} />
            
            {/* Mitt hem */}
            <Route path="/cleaning" element={<ProtectedRoute><CleaningPage /></ProtectedRoute>} />
            <Route path="/shopping" element={<ProtectedRoute><ShoppingPage /></ProtectedRoute>} />
            <Route path="/routines" element={<ProtectedRoute><RoutinesPage /></ProtectedRoute>} />
            <Route path="/maintenance" element={<ProtectedRoute><MaintenancePage /></ProtectedRoute>} />
            <Route path="/projects" element={<ProtectedRoute><ProjectsPage /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute><InventoryPage /></ProtectedRoute>} />
            
            {/* Min planering */}
            <Route path="/goals" element={<ProtectedRoute><GoalsPage /></ProtectedRoute>} />
            <Route path="/habits" element={<ProtectedRoute><HabitsPage /></ProtectedRoute>} />
            <Route path="/reminders" element={<ProtectedRoute><RemindersPage /></ProtectedRoute>} />
            <Route path="/notes" element={<ProtectedRoute><NotesPage /></ProtectedRoute>} />
            <Route path="/todos" element={<ProtectedRoute><TodoPage /></ProtectedRoute>} />
            <Route path="/week-planner" element={<ProtectedRoute><WeekPlannerPage /></ProtectedRoute>} />
            <Route path="/important-dates" element={<ProtectedRoute><ImportantDatesPage /></ProtectedRoute>} />
            
            {/* Min ekonomi & karriär */}
            <Route path="/finance" element={<ProtectedRoute><FinancePage /></ProtectedRoute>} />
            <Route path="/economy-check" element={<ProtectedRoute><EconomyCheckPage /></ProtectedRoute>} />
            <Route path="/savings" element={<ProtectedRoute><SavingsPage /></ProtectedRoute>} />
            <Route path="/debts" element={<ProtectedRoute><DebtsPage /></ProtectedRoute>} />
            <Route path="/career" element={<ProtectedRoute><CareerPage /></ProtectedRoute>} />
            <Route path="/job-search" element={<ProtectedRoute><JobSearchPage /></ProtectedRoute>} />
            <Route path="/cv-help" element={<ProtectedRoute><CvHelpPage /></ProtectedRoute>} />
            
            {/* Mina relationer */}
            <Route path="/relationships" element={<ProtectedRoute><RelationshipsPage /></ProtectedRoute>} />
            <Route path="/relationship-check" element={<ProtectedRoute><RelationshipCheckPage /></ProtectedRoute>} />
            <Route path="/communication" element={<ProtectedRoute><CommunicationPage /></ProtectedRoute>} />
            <Route path="/conflict" element={<ProtectedRoute><ConflictPage /></ProtectedRoute>} />
            <Route path="/couple-exercises" element={<ProtectedRoute><CoupleExercisesPage /></ProtectedRoute>} />
            <Route path="/family" element={<ProtectedRoute><FamilyPage /></ProtectedRoute>} />
            <Route path="/friendship" element={<ProtectedRoute><FriendshipPage /></ProtectedRoute>} />
            <Route path="/toxic-relationships" element={<ProtectedRoute><ToxicRelationshipsPage /></ProtectedRoute>} />
            <Route path="/separation" element={<ProtectedRoute><SeparationPage /></ProtectedRoute>} />
            <Route path="/boundaries" element={<ProtectedRoute><BoundariesPage /></ProtectedRoute>} />
            <Route path="/letters" element={<ProtectedRoute><LettersPage /></ProtectedRoute>} />
            
            {/* Settings & Subscription */}
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
            <Route path="/subscription/success" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
            
            {/* Legacy routes */}
            <Route path="/motivation" element={<ProtectedRoute><MotivationPage /></ProtectedRoute>} />
            
            {/* 404 catch-all */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="bottom-center" />
        <InstallPrompt />
        <UpdatePrompt />
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
